/**
 * TanStack Query hooks for `auto-pause-campaign-when-threshold-hit`.
 * Thin wrappers over ./api. The list hook supports optional campaign_id and
 * action filters which are baked into the query key for correct caching.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	autoPauseQueryKeys,
	evaluate,
	getSummary,
	listPauses,
	type EvaluateBody,
	type EvaluateResponse,
	type ListResponse,
	type PauseAction,
	type SummaryResponse,
} from "./api";

export function useAutoPauseList(params?: {
	campaign_id?: string;
	action?: Exclude<PauseAction, "skip">;
}): UseQueryResult<ListResponse, Error> {
	return useQuery({
		queryKey: autoPauseQueryKeys.list(params),
		queryFn: () => listPauses(params),
	});
}

export function useAutoPauseSummary(): UseQueryResult<SummaryResponse, Error> {
	return useQuery({
		queryKey: autoPauseQueryKeys.summary(),
		queryFn: getSummary,
	});
}

export function useAutoPauseEvaluate(): UseMutationResult<
	EvaluateResponse,
	Error,
	EvaluateBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: evaluate,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: autoPauseQueryKeys.all });
		},
	});
}
